import React, { createRef } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import toast from 'react-hot-toast';
import { Input, Select } from '../../../components';
import { classNames, delay, fromPhotos, sanitizeToNumbers } from '../../../utils';
import { AuthService, BeneficiariesService, GeneralService, PurchasesService } from '../../../services';
import { BackIcon, CheckCircleGreenIcon, CloseIcon, InfoIcon, PhoneIcon, UploadFileIcon } from '../../../assets/icons';
import { Vector4 } from '../../../assets/img';

const APPLY_TO = {
	OWNER: 1,
	BENEFICIARY: 2,
};

// Expresión regular para detectar los números de teléfono
const PHONE_REGEX = /\d{4}(\s|-){0,1}\d{3}(\s|-){0,1}\d{2}(\s|-){0,1}\d{2}\b/gi;
// Expresión regular para detectar los números de teléfono no modificados en el texto
const NE_PHONE_REGEX = /\d{4}(\s|-){0,1}\d{3}(\s|-){0,1}\d{2}(\s|-){0,1}\d{2}(?!<)\b/;

const docsAPS = [
	{ target: 'medical_report', label: 'Informe Médico', isOptional: false },
	{ target: 'dni', label: 'Cédula de Identidad', isOptional: false },
	{ target: 'prescription', label: 'Récipe', isOptional: false },
];

class RequestService extends React.Component {

	state = {
    selectedProduct: null,
    selectedService: null,
    selectedBeneficiary: '',
		applyTo: APPLY_TO.OWNER,
		products: [],
		services: [],
		phoneCodes: [],
		productBeneficiaries: [],
		docsAPS: [],
		form: {
			page: 1,
			lastPage: 1,
		},
		profile: {
			dniType: '',
			dni: '',
			phoneCode: '',
			phone: '',
			zone: '',
		},
		// Archivos opcionales para APS (id: 2)
		dni: '',
		prescription: '',
		medical_report: '',
	}

	confirmModalRef = createRef();
	successModalRef = createRef();
	updateProfileModalRef = createRef();
	uploadModalRef = createRef();
	instructionsModalRef = createRef();
	goBack = createRef();

	componentDidMount() {
    this.goBack.current = document.getElementById('header-goback');
    this.goBack.current.classList.remove('hidden');

		this.getSelects();
		this.getServices();
	}

  componentWillUnmount() {
    this.goBack.current.classList.add('hidden');
  }

	getSelects = async () => {
		Promise.all([GeneralService.getPhoneCodes()])
			.then(res => {
				this.setState({ phoneCodes: res[0] ?? [] });
			})
			.catch(console.log);
	}

	fillProfile = () => {
		this.setState({
			profile: {
				dniType: String(this.state.selectedService?.user?.dni).substring(0,1),
				dni: String(this.state.selectedService?.user?.dni).substring(1),
				phoneCode: String(this.state.selectedService?.user?.phone).substring(0,4),
				phone: String(this.state.selectedService?.user?.phone).substring(4),
				zone: this.state.selectedService?.user?.zone || '',
			},
		});
	}

	isValidProfile = (showError = true) => {
		const onError = (msg) => {
			if (showError) toast.error(msg);
			return false;
		};

		const { profile } = this.state;

		if (!profile.phoneCode)
			return onError('El campo código de área es requerido');

		if (!profile.phone)
			return onError('El campo teléfono es requerido');

		if (!profile.zone)
			return onError('El campo zona es requerido');

		return true;
	}

	updateProfile = async () => {
		if (!this.isValidProfile()) return;

		try {
			this.updateProfileModalRef.current?.close();

			const data = {
				...this.state.selectedService.user,
				...this.state.profile,
				password: '',
			};

			// Verifica si el beneficiario es el titular o uno de sus beneficiarios
			if (Number(this.state.selectedService?.user_id) === Number(this.props.user?.id)) {
				const res = await AuthService.updateProfile(data);
				await this.props.dispatch({ type: 'SET_USER', payload: res });

			} else {
				await BeneficiariesService.update(data);
			}

		} catch (error) {
			console.log(error);
		}

		const isAPS = this.state.selectedProduct?.is_aps;
		let showCall = isAPS;
		const applyCall = !!this.state.selectedProduct?.apply_call && !!this.state.selectedProduct?.instructions;

		// Si es APS y tiene todos los datos no se le muestra la pantalla de instrucciones
		if (isAPS && this.state.dni && this.state.prescription && this.state.medical_report) {
			showCall = false;
		}

		if (applyCall && (!isAPS || (isAPS && showCall))) {
			this.showInstructions(this.state.selectedProduct?.instructions);
		} else {
			this.props.history.replace('historial/servicios');
		}
	}

	changeProfile = (value,target) => {
		this.setState({
			profile: {
				...this.state.profile,
				[target]: value,
			},
		});
	}

	onGoBack = () => {
		if (this.state.selectedProduct) {
			this.setState({ selectedProduct: null });
			return true;
		} else {
			this.props.history.goBack();
			return true;
		}
	}

	getServices = async (page = 1) => {
		try {
			const res = await PurchasesService.getAvailableServices({ page, perPage: 200 });
			const products = new Map([]);

			res.data?.forEach(x => {
				products.set(x.product_id, x.product);
			});

			products.forEach(x => {
				x.services = res.data.filter(s => s.product_id === x.id);
			});

			this.setState({
				products: [ ...products.values() ],
				services: res.data,
				pages: res.pages,
				page,
				selectedProduct: this.state.selectedProduct
					? products.get(this.state.selectedProduct.id)
					: null,
			});

		} catch (error) {
			console.log(error);
			toast.error('Ocurrió un error inesperado');
		}
	}

	selectProduct = (item) => {
		let beneficiaries = new Map([]);
		item.services?.forEach(x => {
			beneficiaries.set(x.user_id, x.user);
		});

		const unique = [];
		beneficiaries.forEach(x => {
			unique.push({ value: x.id, label: x.fullName });
		});

		this.setState({
			selectedProduct: item,
			selectedBeneficiary: null,
			productBeneficiaries: unique,
		});
	}

	selectService = async (service) => {
		// Verifica si el producto tiene cuotas vencidas
		if (moment(service?.purchase_product?.cutoff_at).isSameOrBefore(moment()))
			return toast.error('Para poder solicitar un servicio debe tener sus cuotas pagadas');

		const isAPS = this.state.selectedProduct?.is_aps;

		if (isAPS && !this.state.selectedService) {
			const yearsOld = moment().diff(moment(service?.user?.birthdate), 'years');
			const docs = docsAPS.map(x => ({ ...x, isOptional: x.target === 'dni' && yearsOld < 18 }));
			this.setState({ selectedService: service, docsAPS: docs });
			this.uploadModalRef.current?.showModal();
			return;

		} else {
			this.setState({ selectedService: service });
		}

		// this.redirectToWhatsapp(service);

		if (service.uses_quantity > 0 && (service.uses_available - service.uses_pending) <= 0) {
			return toast.error('No tiene usos disponibles');
		}

		if (service.uses_pending) {
			return toast.error('Usted posee un servicio en proceso. Espere que sea aprobado el anterior.');
		}

		this.confirmModalRef.current?.showModal();
	}

	requestService = async () => {
		this.uploadModalRef.current?.close();
		this.confirmModalRef.current?.close();

		if (!this.state.selectedProduct || !this.state.selectedService) return;
		const isAPS = this.state.selectedProduct?.is_aps;
		const data = { service_id: this.state.selectedService?.id, hasFile: false };

		if (isAPS) {
			data.hasFile = true;
			data.dni = this.state.dni;
			data.prescription = this.state.prescription;
			data.medical_report = this.state.medical_report;
		}

		try {
			await PurchasesService.requestService(data);
			this.successModalRef.current?.showModal();
			await this.getServices();
			this.fillProfile();

			setTimeout(() => {
				this.setState({
					dni: '',
					prescription: '',
					medical_report: '',
				});
				this.successModalRef.current?.close();
				this.uploadModalRef.current?.close();
				this.updateProfileModalRef.current?.showModal();
			}, 4000);

		} catch (error) {
			console.log(error);
		}
	}

	applyTo = (to) => {
		this.setState({ applyTo: to });
	}

  redirectToWhatsapp = (service) => {
		const message = encodeURIComponent(`Hola estoy interesado en el servicio de ${service.name}`);
		const url = `https://api.whatsapp.com/send/?phone=${service.phone}&text=${message}&type=phone_number&app_absent=0`;
		const link = document.createElement('a');
		link.href = url;
		link.click();
		link.remove();
  }

	showInstructions = (item) => {
		const chars = item?.split('\n')?.join(' ') || '';
		const phones = chars.match(PHONE_REGEX) || [];
		let mappedInstructions = chars;

		phones.forEach(x => {
			mappedInstructions = mappedInstructions.replace(NE_PHONE_REGEX, `<>${x}<>`);
		});

		mappedInstructions = mappedInstructions?.split('<>') || [];
		this.setState({ selectedInstructions: mappedInstructions });
		this.instructionsModalRef.current?.showModal();
	}

	goToCall = (phone) => {
		const sanitized = sanitizeToNumbers(phone);
		const link = document.createElement('a');
		link.href = `tel:${sanitized}`;
		link.click();
		link.remove();

		if (this.state.selectedService) {
			this.props.history.replace('historial/servicios');
		}
	}

  uploadFile = async (file, target) => {
		await this.setState({ [target]: file });
		await delay(10);
  }

	cancelUpload = () => {
		this.setState({ selectedService: null, dni: '', prescription: '', medical_report: '' });
		this.uploadModalRef.current?.close();
	}

	render() {

		const { profile, products, selectedBeneficiary, selectedProduct, selectedService, selectedInstructions } = this.state;
		const hasValidDocs = this.state.docsAPS.every(x => x.isOptional ? true : !!this.state[x.target]);

		return (
			<>
				<dialog ref={this.confirmModalRef} className="fixed inset-0 z-50 overflow-y-auto bg-transparent">
					<div className="w-full sm:w-[20rem] bg-white rounded-xl overflow-hidden">
						<div className="py-8 px-5">
							<p className="text-center">¿Estás seguro que deseas usar este servicio?</p>
						</div>

						<div className="flex justify-between h-12 font-semibold">
							<button
								className="flex-1 bg-white p-3"
								onClick={() => {
									this.setState({ selectedItem: null });
									this.confirmModalRef.current?.close();
								}}
							>
								<span>Cancelar</span>
							</button>

							<button className="flex-1 text-blue" onClick={this.requestService}>
								<span>Aceptar</span>
							</button>
						</div>
					</div>
				</dialog>

				<dialog ref={this.successModalRef} className="fixed inset-0 z-50 overflow-y-auto bg-transparent">
					<div
						className="w-full sm:w-[20rem] bg-white rounded-xl overflow-hidden p-4 flex flex-col items-center"
						onClick={() => this.successModalRef.current?.close()}
					>
						<button className="absolute top-3 right-3">
							<img src={CloseIcon} className="w-4 h-4 object-contain" alt="" />
						</button>

						<img src={Vector4} className="w-32 h-32 object-contain" alt="" />
						<p className="text-center font-semibold">
							Su servicio ha sido solicitado con éxito
						</p>
					</div>
				</dialog>

				<dialog ref={this.uploadModalRef} className="fixed inset-0 z-50 overflow-y-auto bg-transparent">
					<div className="w-full sm:w-[24rem] bg-white rounded-xl overflow-hidden flex flex-col gap-4">
						<p className="p-4 pb-0">
							<span className="font-bold">SI POSEE INFORME MÉDICO Y REFERENCIAS POR FAVOR ADJUNTAR</span>
							<span>, y solicitar el servicio.</span>
						</p>

						<div className="px-4 space-y-2">
							{docsAPS.map((doc, index) => {
								const isUploaded = !!this.state[doc.target];

								return (
									<label key={index} className="flex justify-between items-center w-full">
										<div className="flex items-center gap-2">
											<div
												className={classNames(
													'w-4 h-4 rounded border flex justify-center items-center',
													isUploaded ? 'bg-green border-green' : 'bg-[#F2F2F2] border-[#BBBBBB]'
												)}
											>
												{isUploaded && (
													<img src={CheckCircleGreenIcon} className="w-3 h-3 object-contain" alt="" />
												)}
											</div>
											<p className="flex font-semibold gap-1">
												<span>{ doc.label }</span>
												{!doc.isOptional && <span className="text-red">*</span>}
											</p>
										</div>
										<img src={UploadFileIcon} className="w-6 h-6 object-contain invert" alt="" />

										<input
											type="file"
											className="hidden"
											accept="application/pdf,image/jpeg,image/png"
											onChange={e => this.uploadFile(e.target.files[0], doc.target)}
										/>
									</label>
								);
							})}
						</div>

						<p className="mt-2 px-4">
							<span>Si NO posee informe médico presione </span>
							<span
								className="text-blue underline font-bold cursor-pointer"
								onClick={() => this.selectService(selectedService)}
							>
								AQUÍ
							</span>
							<span> para conversar con nuestros ejecutivos</span>
						</p>

						<div className="flex justify-between h-12 font-semibold">
							<button className="flex-1" onClick={this.cancelUpload}>
								<span className="font-semibold">Cancelar</span>
							</button>

							<button
								className="flex-1"
								onClick={() => hasValidDocs
									? this.selectService(selectedService)
									: toast.error('Debe subir todos los documentos para continuar')
								}
							>
								<span className="font-semibold">Continuar</span>
							</button>
						</div>
					</div>
				</dialog>

				<dialog ref={this.updateProfileModalRef} className="fixed inset-0 z-50 overflow-y-auto bg-transparent">
					<div className="w-full sm:w-[24rem] bg-white p-4 rounded-xl overflow-hidden flex flex-col gap-4">
						<p className="text-lg leading-5 font-semibold text-blue">
							Para completar la solicitud por favor necesitamos que actualice sus datos
						</p>

						<div className="flex flex-row items-center gap-x-2 w-full">
							<div className="flex-[3]">
								<Select
									items={this.state.phoneCodes}
									value={this.state.form.phoneCode}
									onValueChange={v => this.changeProfile(v,'phoneCode')}
								/>
							</div>
							<div className="flex-[5]">
								<Input
									placeholder="Número de teléfono"
									value={this.state.form.phone}
									onValueChange={v => this.changeProfile(sanitizeToNumbers(v),'phone')}
									maxLength={7}
								/>
							</div>
						</div>

						<Input
							placeholder="Zona"
							value={profile.zone}
							onValueChange={v => this.changeProfile(v,'zone')}
						/>

						<div style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-end', marginTop: 20 }}>
							<button onClick={this.updateProfile}>
								<span className="font-semibold">Siguiente</span>
							</button>
						</div>
					</div>
				</dialog>

				<dialog
					ref={this.instructionsModalRef}
					className="fixed inset-0 z-50 overflow-y-auto bg-transparent"
					onClose={() => {
						this.setState({ selectedInstructions: null });
						this.instructionsModalRef.current?.close();
						if (this.state.selectedService) {
							this.props.history.replace('/historial/servicios');
						}
					}}
				>
					<div
						className="w-[20rem] bg-white rounded-xl overflow-hidden p-4 flex flex-col items-center"
						onClick={() => this.instructionsModalRef.current?.close()}
					>
						<button className="absolute top-3 right-3">
							<img src={CloseIcon} className="w-4 h-4 object-contain" alt="" />
						</button>
						<div className="w-16 h-16 object-contain bg-blue rounded-xl flex justify-center items-center mb-5">
							<img src={PhoneIcon} className="w-12 h-12 object-contain invert" alt="" />
						</div>

						<p>
							{selectedInstructions?.map((row, index) => {
								if (PHONE_REGEX.test(row)) return (
									<button key={index} onClick={() => this.goToCall(row)}>
										<span className="text-blue font-bold underline">{ row }</span>
									</button>
								);

								return <span key={index}>{ row || '' }</span>;
							})}
						</p>
					</div>
				</dialog>

				{(!!selectedProduct && !!selectedProduct?.instructions) && (
					<button
						style={{ position: 'absolute', right: 10 }}
						onClick={() => this.showInstructions(selectedProduct.instructions)}
					>
						<img src={InfoIcon} className="w-6 h-6 object-contain" alt="" />
					</button>
				)}

				{!selectedProduct ? (
					<>
						<div className="m-5 mt-8 lg:mt-5">
							<p className="text-center font-semibold">Seleccione el producto del que desea el servicio</p>
						</div>
						<div className="max-w-[600px] mx-auto grid grid-cols-2 gap-4">
							{products.map((item, i) => (
								<CardItem
									key={`product-${i}`}
									item={item}
									onClick={() => this.selectProduct(item)}
								/>
							))}

							{!products.length && (
								<p className="font-semibold text-gray3 text-center my-10">
									No hay productos
								</p>
							)}
						</div>
					</>
				) : (
					<>
						<button className="flex items-center gap-2 px-2 py-1 mt-8 lg:mt-0 rounded-full bg-darkBlue" onClick={() => this.setState({ selectedProduct: null })}>
							<img src={BackIcon} className="w-4 h-4 object-contain" alt="" />
							<span className="text-center text-white text-sm font-semibold">Seleccionar producto</span>
						</button>

						<div className="max-w-[600px] mx-auto">
							<div>
								<div className="m-5">
									<p className="text-center text-darkBlue font-semibold">
										Indica el beneficiario y selecciona el servicio que necesitas
									</p>
								</div>

								<div className="w-60 mx-auto mb-5">
									<Select
										placeholder="Seleccione"
										items={this.state.productBeneficiaries}
										value={selectedBeneficiary || ''}
										onValueChange={v => this.setState({ selectedBeneficiary: +v })}
									/>
								</div>

								{!selectedProduct.services.length && (
									<p className="text-center font-semibold">
										No hay servicios disponibles
									</p>
								)}

								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									{selectedProduct.services.filter(x => selectedBeneficiary ? (x.user_id === selectedBeneficiary) : false).map((service, index) => {
										// const isSelected = selectedService?.id === service.id;
										const remains = service.uses_available - service.uses_pending;
										const isUnlimited = service.uses_quantity === 0;

										return (
											<div key={index} className="border flex flex-col p-2 rounded-lg border-gray6 relative">
												<div className="flex flex-1 items-start">
													<img
														src={fromPhotos(service.service?.photo)}
														className="w-20 h-20 object-cover rounded-md"
														alt=""
													/>
													<div className="pl-2">
														<p className="font-semibold leading-5 text-darkBlue line-clamp-2" title={service?.service?.name}>
															{ service?.service?.name }
														</p>
														<p className="text-sm font-semibold mt-1">
															{ service?.user?.fullName }
														</p>
													</div>
												</div>

												<div className="flex justify-between items-end">
													<div className="flex gap-2 items-center">
														<button
															className="px-4 py-1 rounded-full flex bg-yellow"
															onClick={() => this.selectService(service)}
														>
															<span className="font-semibold text-sm text-black">
																Seleccionar
															</span>
														</button>

														{!!service?.service?.instructions && (
															<button onClick={() => this.showInstructions(service.service.instructions)}>
																<img src={InfoIcon} className="w-6 h-6 object-contain" alt="" />
															</button>
														)}
													</div>

													<div className="bg-darkBlue rounded-xl text-white px-2 py-1">
														<span className="text-3xl leading-8 font-semibold">{ isUnlimited ? '∞' : remains }</span>
														<span className="text-sm"> Serv</span>
													</div>
												</div>
											</div>
										);
									})}
								</div>
							</div>
						</div>
					</>
				)}
			</>
		);
	}
}

const CardItem = ({ item, onClick }) => {
	return (
		<div className="cursor-pointer" onClick={onClick}>
			<div className="bg-white rounded-lg overflow-hidden gap-4 border border-gray6">
				<img
					src={fromPhotos(item?.photo)}
					className="aspect-video object-cover w-full"
					alt=""
				/>
				<div className="p-2">
					<p className="leading-5 font-semibold text-left line-clamp-2" title={item.name}>
						{ item.name }
					</p>

					{!!item?.description && (
						<p className="text-sm mt-1 line-clamp-2" title={item.description}>
							{ item.description }
						</p>
					)}
				</div>
			</div>
		</div>
	);
};

export default connect(state => ({
	user: state.user,
}))(RequestService);
